import { useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { formatDate as formatIsoDate } from '@/lib/utils';
import { toast } from 'sonner';
import {
  ExternalLink,
  FolderOpen,
  Loader2,
  AlertTriangle,
  Copy,
  Check,
  Pencil,
  Trash2,
  ChevronRight,
  Home,
} from 'lucide-react';
import { useAccountDriveFiles, useSetAccountDriveFolder } from '../hooks/useAccountDriveFiles';
import type { DriveFile } from '../api';
import { formatBytes } from '@/lib/utils';

interface DriveFolderSectionProps {
  accountId: string;
}

function extractFolderId(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const urlMatch = trimmed.match(/\/folders\/([a-zA-Z0-9_-]+)/);
  if (urlMatch) return urlMatch[1];
  const queryMatch = trimmed.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  if (queryMatch) return queryMatch[1];
  if (/^[a-zA-Z0-9_-]{10,}$/.test(trimmed)) return trimmed;
  return null;
}

function formatDate(iso?: string): string {
  if (!iso) return '';
  return formatIsoDate(iso);
}

function CopyableEmail({ email }: { email: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(email);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Failed to copy');
    }
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      className="inline-flex items-center gap-1.5 rounded border bg-background px-2 py-0.5 font-mono text-xs hover:bg-muted"
      title="Copy service account email"
    >
      <span>{email}</span>
      {copied ? <Check className="h-3 w-3 text-green-600" /> : <Copy className="h-3 w-3" />}
    </button>
  );
}

function FolderForm({
  accountId,
  initial,
  onDone,
}: {
  accountId: string;
  initial?: string | null;
  onDone?: () => void;
}) {
  const [input, setInput] = useState(initial ?? '');
  const setFolder = useSetAccountDriveFolder(accountId);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed) {
      // Empty → clear folder
      await setFolder.mutateAsync(null);
      onDone?.();
      return;
    }
    const id = extractFolderId(trimmed);
    if (!id) {
      toast.error("That doesn't look like a Drive folder URL or ID.");
      return;
    }
    await setFolder.mutateAsync(id);
    onDone?.();
  };

  return (
    <form onSubmit={handleSave} className="space-y-2">
      <Label htmlFor="drive-folder-input" className="text-xs">
        Drive folder URL or ID
      </Label>
      <div className="flex gap-2">
        <Input
          id="drive-folder-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="https://drive.google.com/drive/folders/..."
          className="text-sm"
        />
        <Button type="submit" size="sm" disabled={setFolder.isPending}>
          {setFolder.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
        </Button>
        {onDone && (
          <Button type="button" size="sm" variant="ghost" onClick={onDone}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
}

function DriveFileRow({
  file,
  onEnterFolder,
  onPreviewFile,
}: {
  file: DriveFile;
  onEnterFolder: (id: string, name: string) => void;
  onPreviewFile: (file: DriveFile) => void;
}) {
  const isFolder = file.mimeType === 'application/vnd.google-apps.folder';
  const meta = [formatDate(file.modifiedTime), file.size ? formatBytes(Number(file.size)) : '']
    .filter(Boolean)
    .join(' · ');

  const inner = (
    <>
      {file.iconLink ? (
        <img src={file.iconLink} alt="" className="h-4 w-4 shrink-0" />
      ) : (
        <FolderOpen className="h-4 w-4 text-muted-foreground shrink-0" />
      )}
      <span className="text-sm flex-1 truncate text-foreground text-left">{file.name}</span>
      {meta && <span className="text-xs text-muted-foreground shrink-0">{meta}</span>}
      {isFolder ? (
        <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      ) : (
        <ExternalLink className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 shrink-0" />
      )}
    </>
  );

  return (
    <button
      type="button"
      onClick={() => (isFolder ? onEnterFolder(file.id, file.name) : onPreviewFile(file))}
      className="group flex w-full items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted/50 transition-colors"
    >
      {inner}
    </button>
  );
}

function DrivePreviewModal({
  file,
  onClose,
}: {
  file: DriveFile | null;
  onClose: () => void;
}) {
  if (!file) return null;
  // Google's universal preview embed works for Docs, Sheets, Slides, PDFs, images, video.
  const previewSrc = `https://drive.google.com/file/d/${file.id}/preview`;
  return (
    <Dialog open={!!file} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-5xl w-[90vw] h-[85vh] p-0 flex flex-col">
        <DialogHeader className="px-4 py-3 border-b flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-sm truncate pr-4">{file.name}</DialogTitle>
          {file.webViewLink && (
            <Button asChild size="sm" variant="ghost" className="shrink-0">
              <a href={file.webViewLink} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open in Drive
              </a>
            </Button>
          )}
        </DialogHeader>
        <iframe
          src={previewSrc}
          title={file.name}
          className="flex-1 w-full border-0"
          allow="autoplay"
        />
      </DialogContent>
    </Dialog>
  );
}

type Crumb = { id: string; name: string };

export default function DriveFolderSection({ accountId }: DriveFolderSectionProps) {
  // Breadcrumb stack: empty = at root (linked folder). Each entry is a child folder drilled into.
  const [breadcrumb, setBreadcrumb] = useState<Crumb[]>([]);
  const currentFolderId = breadcrumb.length > 0 ? breadcrumb[breadcrumb.length - 1].id : null;
  const { data, isLoading, isError, error, isFetching } = useAccountDriveFiles(
    accountId,
    currentFolderId,
  );
  const [editing, setEditing] = useState(false);
  const [confirmUnlink, setConfirmUnlink] = useState(false);
  const [previewFile, setPreviewFile] = useState<DriveFile | null>(null);
  const setFolder = useSetAccountDriveFolder(accountId);

  const status = data?.status;
  const serviceAccountEmail = data?.serviceAccountEmail;

  const enterFolder = (id: string, name: string) => {
    setBreadcrumb((prev) => [...prev, { id, name }]);
  };

  const jumpTo = (index: number) => {
    // index -1 = root, 0..n = breadcrumb position
    setBreadcrumb((prev) => prev.slice(0, index + 1));
  };

  const headerActions =
    status === 'ok' && !editing ? (
      <div className="flex items-center gap-1">
        {data?.folderUrl && (
          <Button asChild size="sm" variant="ghost">
            <a href={data.folderUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open in Drive
            </a>
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
          <Pencil className="h-3.5 w-3.5 mr-1" /> Change
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setConfirmUnlink(true)}
          disabled={setFolder.isPending}
        >
          <Trash2 className="h-3.5 w-3.5 mr-1" /> Unlink
        </Button>
      </div>
    ) : null;

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle>Google Drive Folder</CardTitle>
          <CardDescription>
            Read-only view of the Drive folder attached to this account.
          </CardDescription>
        </div>
        {headerActions}
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}

        {isError && !isLoading && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Could not load Drive files</AlertTitle>
            <AlertDescription>
              {error instanceof Error ? error.message : 'Unknown error'}
            </AlertDescription>
          </Alert>
        )}

        {!isLoading && !isError && status === 'not_configured' && !editing && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              No Drive folder attached to this account yet.
            </p>
            <FolderForm accountId={accountId} />
            {serviceAccountEmail && (
              <p className="text-xs text-muted-foreground">
                After setting a folder, share it with{' '}
                <CopyableEmail email={serviceAccountEmail} /> (viewer is enough).
              </p>
            )}
          </div>
        )}

        {!isLoading && !isError && editing && (
          <FolderForm
            accountId={accountId}
            initial={data?.folderId ?? ''}
            onDone={() => setEditing(false)}
          />
        )}

        {!isLoading && !isError && !editing && status === 'forbidden' && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Folder not accessible</AlertTitle>
            <AlertDescription className="space-y-2">
              <p className="text-sm">
                The folder is set, but the service account can't read it yet.
              </p>
              {serviceAccountEmail && (
                <p className="text-sm">
                  Share the folder with <CopyableEmail email={serviceAccountEmail} /> (Viewer
                  access is enough), then reload.
                </p>
              )}
              <div className="flex gap-2 pt-1">
                {data?.folderUrl && (
                  <Button asChild size="sm" variant="outline">
                    <a href={data.folderUrl} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="h-3.5 w-3.5 mr-1" /> Open folder
                    </a>
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                  Change folder
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {!isLoading && !isError && !editing && status === 'not_found' && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Folder not found</AlertTitle>
            <AlertDescription className="space-y-2">
              <p className="text-sm">
                The folder ID on this account doesn't resolve. It may have been deleted or the
                ID is wrong.
              </p>
              <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                Change folder
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {!isLoading && !isError && !editing && status === 'ok' && (
          <>
            {breadcrumb.length > 0 && (
              <div className="flex items-center flex-wrap gap-1 text-xs text-muted-foreground border-b pb-2">
                <button
                  type="button"
                  onClick={() => jumpTo(-1)}
                  className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 hover:bg-muted hover:text-foreground"
                >
                  <Home className="h-3 w-3" /> Root
                </button>
                {breadcrumb.map((c, i) => (
                  <span key={c.id} className="inline-flex items-center gap-1">
                    <ChevronRight className="h-3 w-3" />
                    <button
                      type="button"
                      onClick={() => jumpTo(i)}
                      className={`rounded px-1.5 py-0.5 hover:bg-muted ${
                        i === breadcrumb.length - 1
                          ? 'text-foreground font-medium'
                          : 'hover:text-foreground'
                      }`}
                    >
                      {c.name}
                    </button>
                  </span>
                ))}
                {isFetching && <Loader2 className="h-3 w-3 animate-spin ml-1" />}
              </div>
            )}
            {data!.files.length === 0 ? (
              <p className="text-sm text-muted-foreground">This folder is empty.</p>
            ) : (
              <div className="space-y-0.5">
                {data!.files.map((f) => (
                  <DriveFileRow
                    key={f.id}
                    file={f}
                    onEnterFolder={enterFolder}
                    onPreviewFile={setPreviewFile}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </CardContent>

      <DrivePreviewModal file={previewFile} onClose={() => setPreviewFile(null)} />

      <AlertDialog open={confirmUnlink} onOpenChange={setConfirmUnlink}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unlink Drive folder?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the folder reference from the account. The folder itself is not
              touched in Google Drive.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                await setFolder.mutateAsync(null);
                setConfirmUnlink(false);
              }}
            >
              Unlink
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
